import React from 'react';

function formatBytes(bytes) {
  if (bytes == null) return 'N/A';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} bytes`;
}

const FLAG_COLORS = {
  keep: '#22c55e',
  archive: '#f59e0b',
  delete: '#ef4444',
  unreviewed: '#cbd5e1',
};

export default function StorageGauge({ storage }) {
  const { file_size_available, file_size_unit_requires_confirmation, by_flag, total_assets } = storage;

  // Count-based progress bar (always available, even without file sizes)
  const reviewed = total_assets - (by_flag.unreviewed?.count || 0);
  const reviewedPct = total_assets > 0 ? (reviewed / total_assets * 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Rationalization Progress</h3>
        <span className="text-xs text-slate-500">
          {reviewed.toLocaleString()} of {total_assets.toLocaleString()} assets reviewed ({reviewedPct.toFixed(0)}%)
        </span>
      </div>

      {/* Stacked progress bar by flag */}
      <div className="w-full bg-slate-100 rounded-full h-4 flex overflow-hidden mb-3">
        {['keep', 'archive', 'delete', 'unreviewed'].map(flag => {
          const count = by_flag[flag]?.count || 0;
          const pct = total_assets > 0 ? (count / total_assets * 100) : 0;
          if (pct === 0) return null;
          return (
            <div
              key={flag}
              style={{ width: `${pct}%`, backgroundColor: FLAG_COLORS[flag] }}
              className="h-full transition-all duration-300"
              title={`${flag}: ${count.toLocaleString()} assets`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        {['keep', 'archive', 'delete', 'unreviewed'].map(flag => {
          const count = by_flag[flag]?.count || 0;
          return (
            <div key={flag} className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FLAG_COLORS[flag] }} />
              <span className="text-slate-600 capitalize">{flag}</span>
              <span className="text-slate-400 font-medium">{count.toLocaleString()}</span>
              {file_size_available && by_flag[flag]?.bytes > 0 && (
                <span className="text-slate-400">({formatBytes(by_flag[flag].bytes)})</span>
              )}
            </div>
          );
        })}
      </div>

      {!file_size_available && (
        <div className="mt-3 text-xs text-slate-400 bg-slate-50 rounded p-2">
          {file_size_unit_requires_confirmation
            ? 'Storage sizes are paused because the numeric file-size unit is not declared. Confirm whether the source uses bytes, KB, MB, or GB before using storage totals.'
            : 'Storage sizes not available — no file size column in this export. Progress is shown by asset count only.'}
        </div>
      )}
    </div>
  );
}
